import React, { useEffect, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
// Pagination is temporarily disabled: ChevronLeft and ChevronRight remain documented here for restoration.
import { ArrowDown, ArrowUp, CirclePause, Save, Trash2, UserMinus } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { OdrLoadingSpinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';
import { useUser } from '@/store/useUser';

import {
  EcmAddParticipantDialog,
  EcmExportButton,
  EcmFieldSettingsMenu,
  EcmRecordActionButton,
  EcmResetButton,
  ECM_REMOVE_CENSUS_PARTICIPANT_MUTATION,
  ECM_REQUEST_DEACTIVATION_MUTATION,
  ECM_UPDATE_DETERMINATION_MUTATION,
  exportEcmDataset,
  resolveVisibleColumnIds,
} from './EcmDashboardShared';

export const ECM_CENSUS_QUERY = gql`
  query EcmCensus($month: Int!, $year: Int!) {
    ecmCensus(month: $month, year: $year) {
      dateRangeEnd
      dateRangeStart
      facilities
      lcms
      programs
      qas
      totalCount
      items {
        address
        authorizationEndDate
        censusStart
        cid
        cin
        determination
        dischargeDate
        dischargedThisMonth
        dob
        facility
        firstName
        healthPlan
        lastName
        lcm
        notes
        odrparticipant
        participantId
        participantIds
        phoneNumber
        program
        provider
        qa
      }
    }
  }
`;

export const ECM_CENSUS_ASSIGNEE_OPTIONS_QUERY = gql`
  query EcmCensusAssigneeOptions {
    ecmCensusAssigneeOptions {
      lcms {
        name
        username
      }
      qas {
        name
        username
      }
    }
  }
`;

export const UPDATE_ECM_CENSUS_PARTICIPANT_MUTATION = gql`
  mutation UpdateEcmCensusParticipant($input: EcmCensusParticipantEditInput!, $odrparticipant: Int!) {
    updateEcmCensusParticipant(input: $input, odrparticipant: $odrparticipant) {
      address
      authorizationEndDate
      lcm
      notes
      odrparticipant
      phoneNumber
      qa
      success
    }
  }
`;

export const ADD_ECM_CENSUS_PARTICIPANT_MUTATION = gql`
  mutation AddEcmCensusParticipant($odrparticipant: Int!) {
    addEcmCensusParticipant(odrparticipant: $odrparticipant) {
      alreadyActive
      odrparticipant
      success
    }
  }
`;

const ALL_FILTER_VALUE = '__all__';
const PAGE_SIZE = 100;

type SortDirection = 'asc' | 'desc';
type SortField =
  | 'authorizationEndDate'
  | 'censusStart'
  | 'cid'
  | 'cin'
  | 'dob'
  | 'facility'
  | 'firstName'
  | 'healthPlan'
  | 'lastName'
  | 'lcm'
  | 'program'
  | 'provider'
  | 'qa';

type EcmCensusParticipant = {
  address?: string | null;
  authorizationEndDate?: string | null;
  censusStart?: string | null;
  cid?: string | null;
  cin?: string | null;
  determination?: string | null;
  dischargeDate?: string | null;
  dischargedThisMonth?: boolean | null;
  dob?: string | null;
  facility?: string | null;
  firstName?: string | null;
  healthPlan?: string | null;
  lastName?: string | null;
  lcm?: string | null;
  notes?: string | null;
  odrparticipant: number;
  participantId?: number | null;
  participantIds?: Array<number | string | null> | null;
  phoneNumber?: string | null;
  program?: string | null;
  provider?: string | null;
  qa?: string | null;
};

type EcmCensusResult = {
  dateRangeEnd?: string | null;
  dateRangeStart?: string | null;
  facilities?: string[] | null;
  items?: EcmCensusParticipant[] | null;
  lcms?: string[] | null;
  programs?: string[] | null;
  qas?: string[] | null;
  totalCount?: number | null;
};

type EcmCensusQueryResult = {
  ecmCensus: EcmCensusResult;
};

type EcmCensusQueryVariables = {
  month: number;
  year: number;
};

type EcmCensusAssignee = {
  name?: string | null;
  username?: string | null;
};

type EcmCensusAssigneeOptionsResult = {
  ecmCensusAssigneeOptions?: {
    lcms?: EcmCensusAssignee[] | null;
    qas?: EcmCensusAssignee[] | null;
  } | null;
};

type EcmCensusEditValues = {
  address: string;
  authorizationEndDate: string;
  lcm: string;
  notes: string;
  phoneNumber: string;
  qa: string;
};

type MonthOption = {
  label: string;
  value: string;
};

const normalizeValue = (value: unknown) => `${value || ''}`.trim();
const normalizeLowerCase = (value: unknown) => normalizeValue(value).toLowerCase();
const PARTICIPANT_LINK_CLASS_NAME = 'text-sky-700 hover:text-sky-900 hover:underline';

const toParticipantPath = (item: EcmCensusParticipant) => {
  const odrparticipant = Number(item.odrparticipant);
  if (Number.isInteger(odrparticipant) && odrparticipant > 0) {
    return `/participants/${odrparticipant}`;
  }

  return null;
};

const padNumber = (value: number) => `${value}`.padStart(2, '0');

const toMonthKey = (year: number, month: number) => `${year}-${padNumber(month)}`;

const getCurrentMonthKey = () => {
  const currentDate = new Date();
  return toMonthKey(currentDate.getUTCFullYear(), currentDate.getUTCMonth() + 1);
};

const parseMonthKey = (monthKey: string) => {
  const [rawYear, rawMonth] = monthKey.split('-');
  const year = Number(rawYear);
  const month = Number(rawMonth);

  if (Number.isInteger(year) && Number.isInteger(month) && month >= 1 && month <= 12) {
    return { month, year };
  }

  const [fallbackYear, fallbackMonth] = getCurrentMonthKey().split('-');
  return {
    month: Number(fallbackMonth),
    year: Number(fallbackYear),
  };
};

const parseIsoDate = (value: string | null | undefined) => {
  const normalizedValue = normalizeValue(value);
  const match = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return null;
  }

  return {
    day: Number(match[3]),
    month: Number(match[2]),
    year: Number(match[1]),
  };
};

const formatMonthLabel = (year: number, month: number) => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    timeZone: 'UTC',
    year: 'numeric',
  }).format(new Date(Date.UTC(year, month - 1, 1)));
};

const formatDate = (value: string | null | undefined) => {
  const parsedDate = parseIsoDate(value);
  if (!parsedDate) {
    return '—';
  }

  return `${padNumber(parsedDate.month)}/${padNumber(parsedDate.day)}/${parsedDate.year}`;
};

const buildMonthOptions = (startValue: string | null | undefined, endValue: string | null | undefined) => {
  const startDate = parseIsoDate(startValue);
  const endDate = parseIsoDate(endValue);
  if (!startDate || !endDate) {
    return [] as MonthOption[];
  }

  const options: MonthOption[] = [];
  let year = endDate.year;
  let month = endDate.month;

  while (year > startDate.year || (year === startDate.year && month >= startDate.month)) {
    options.push({
      label: formatMonthLabel(year, month),
      value: toMonthKey(year, month),
    });

    month -= 1;
    if (month >= 1) {
      continue;
    }

    month = 12;
    year -= 1;
  }

  return options;
};

const getSelectedMonthLabel = (monthKey: string, monthOptions: MonthOption[]) => {
  const option = monthOptions.find((item) => item.value === monthKey);
  if (option) {
    return option.label;
  }

  const { month, year } = parseMonthKey(monthKey);
  return formatMonthLabel(year, month);
};

const getSearchableText = (item: EcmCensusParticipant) => {
  return [
    item.address,
    item.cid,
    item.cin,
    item.facility,
    item.firstName,
    item.healthPlan,
    item.lastName,
    item.lcm,
    item.notes,
    item.phoneNumber,
    item.program,
    item.provider,
    item.qa,
  ]
    .map((value) => normalizeLowerCase(value))
    .filter(Boolean)
    .join(' ');
};

const matchesSelectedValue = (candidateValue: string | null | undefined, selectedValue: string) => {
  if (selectedValue === ALL_FILTER_VALUE) {
    return true;
  }

  return normalizeValue(candidateValue) === selectedValue;
};

const compareValues = (left: string, right: string, direction: SortDirection) => {
  const comparison = left.localeCompare(right, undefined, {
    numeric: true,
    sensitivity: 'base',
  });

  if (direction === 'asc') {
    return comparison;
  }

  return comparison * -1;
};

const getSortValue = (item: EcmCensusParticipant, field: SortField) => {
  if (field === 'authorizationEndDate') {
    return normalizeValue(item.authorizationEndDate);
  }

  if (field === 'censusStart') {
    return normalizeValue(item.censusStart);
  }

  if (field === 'cid') {
    return normalizeValue(item.cid);
  }

  if (field === 'cin') {
    return normalizeValue(item.cin);
  }

  if (field === 'dob') {
    return normalizeValue(item.dob);
  }

  if (field === 'facility') {
    return normalizeValue(item.facility);
  }

  if (field === 'firstName') {
    return normalizeValue(item.firstName);
  }

  if (field === 'healthPlan') {
    return normalizeValue(item.healthPlan);
  }

  if (field === 'lcm') {
    return normalizeValue(item.lcm);
  }

  if (field === 'program') {
    return normalizeValue(item.program);
  }

  if (field === 'provider') {
    return normalizeValue(item.provider);
  }

  if (field === 'qa') {
    return normalizeValue(item.qa);
  }

  return normalizeValue(item.lastName);
};

const SortableHeader = ({
  field,
  label,
  onSort,
  sortDirection,
  sortField,
}: {
  field: SortField;
  label: string;
  onSort: (field: SortField) => void;
  sortDirection: SortDirection;
  sortField: SortField;
}) => {
  const isActive = sortField === field;
  const isAscending = isActive && sortDirection === 'asc';
  const isDescending = isActive && sortDirection === 'desc';
  const nextDirection = isAscending ? 'descending' : 'ascending';

  return (
    <button
      aria-label={`Sort by ${label} ${nextDirection}`}
      type="button"
      className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.04em] text-slate-700 hover:text-slate-900"
      onClick={() => onSort(field)}
    >
      <span>{label}</span>
      <span aria-hidden="true" className="inline-flex items-center gap-0.5">
        <ArrowUp
          className={cn('h-3 w-3', isAscending ? 'text-[var(--azure-primary)]' : 'text-slate-400')}
          data-testid={`ecm-census-sort-${field}-ascending`}
        />
        <ArrowDown
          className={cn('h-3 w-3', isDescending ? 'text-[var(--azure-primary)]' : 'text-slate-400')}
          data-testid={`ecm-census-sort-${field}-descending`}
        />
      </span>
    </button>
  );
};

const FilterLabel = ({ children }: { children: React.ReactNode }) => {
  return <div className="text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">{children}</div>;
};

const TextCell = ({
  className = '',
  value,
}: {
  className?: string;
  value: string | null | undefined;
}) => {
  const displayValue = normalizeValue(value) || '—';

  return (
    <span className={`block truncate ${className}`} title={displayValue}>
      {displayValue}
    </span>
  );
};

const ECM_CENSUS_COLUMNS = [
  { id: 'firstName', label: 'First Name' },
  { id: 'lastName', label: 'Last Name' },
  { id: 'program', label: 'Program' },
  { id: 'provider', label: 'Provider' },
  { id: 'censusStart', label: 'Date Entered' },
  { id: 'authorizationEndDate', label: 'Auth End Date' },
  { id: 'address', label: 'Address' },
  { id: 'phoneNumber', label: 'Phone #' },
  { id: 'notes', label: 'Notes' },
  { id: 'cid', label: 'CID' },
  { id: 'cin', label: 'CIN' },
  { id: 'healthPlan', label: 'Health Plan Provider' },
  { id: 'lcm', label: 'LCM' },
  { id: 'dob', label: 'DOB' },
  { id: 'qa', label: 'QA' },
] as const;

type CensusDetailFormat = 'boolean' | 'date' | 'list';

type CensusDetailField = {
  format?: CensusDetailFormat;
  fullWidth?: boolean;
  id: keyof EcmCensusParticipant;
  label: string;
};

const ECM_CENSUS_DETAIL_FIELDS: CensusDetailField[] = [
  { id: 'firstName', label: 'First Name' },
  { id: 'lastName', label: 'Last Name' },
  { id: 'dob', label: 'DOB', format: 'date' },
  { id: 'cid', label: 'CID' },
  { id: 'cin', label: 'CIN' },
  { id: 'odrparticipant', label: 'ODR Participant' },
  { id: 'participantId', label: 'Participant ID' },
  { id: 'participantIds', label: 'Participant IDs', format: 'list' },
  { id: 'program', label: 'Program' },
  { id: 'provider', label: 'Provider' },
  { id: 'censusStart', label: 'Date Entered', format: 'date' },
  { id: 'authorizationEndDate', label: 'Authorization End Date', format: 'date' },
  { id: 'determination', label: 'Determination' },
  { id: 'dischargeDate', label: 'Discharge Date', format: 'date' },
  { id: 'dischargedThisMonth', label: 'Discharged This Month', format: 'boolean' },
  { id: 'healthPlan', label: 'Health Plan Provider' },
  { id: 'facility', label: 'Facility' },
  { id: 'lcm', label: 'LCM' },
  { id: 'qa', label: 'QA' },
  { id: 'phoneNumber', label: 'Phone Number' },
  { id: 'address', label: 'Address', fullWidth: true },
  { id: 'notes', label: 'Notes', fullWidth: true },
];

const formatDetailValue = (value: unknown, format?: CensusDetailFormat) => {
  if (format === 'date') {
    return formatDate(typeof value === 'string' ? value : null);
  }

  if (format === 'boolean') {
    if (typeof value !== 'boolean') {
      return '—';
    }

    return value ? 'Yes' : 'No';
  }

  if (format === 'list') {
    if (!Array.isArray(value)) {
      return '—';
    }

    return value.map((item) => normalizeValue(item)).filter(Boolean).join(', ') || '—';
  }

  return normalizeValue(value) || '—';
};

const getCensusEditValues = (item: EcmCensusParticipant): EcmCensusEditValues => ({
  address: normalizeValue(item.address),
  authorizationEndDate: normalizeValue(item.authorizationEndDate).slice(0, 10),
  lcm: normalizeValue(item.lcm),
  notes: normalizeValue(item.notes),
  phoneNumber: normalizeValue(item.phoneNumber),
  qa: normalizeValue(item.qa),
});

const isInteractiveRowTarget = (target: EventTarget | null) => {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(target.closest('a, button, input, select, textarea, [role="menuitem"]'));
};

export const EcmDashboardCensusView = ({ readOnly = false }: { readOnly?: boolean }) => {
  const { ecmDashboardColumnPreferenceMap, updateEcmDashboardColumnPreferences } = useUser();
  const [selectedMonthKey, setSelectedMonthKey] = useState(getCurrentMonthKey);
  const [searchText, setSearchText] = useState('');
  const [programFilter, setProgramFilter] = useState(ALL_FILTER_VALUE);
  const [facilityFilter, setFacilityFilter] = useState(ALL_FILTER_VALUE);
  const [qaFilter, setQaFilter] = useState(ALL_FILTER_VALUE);
  const [lcmFilter, setLcmFilter] = useState(ALL_FILTER_VALUE);
  const [sortField, setSortField] = useState<SortField>('lastName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [activeRowActionKey, setActiveRowActionKey] = useState<string | null>(null);
  const [selectedDetailsItem, setSelectedDetailsItem] = useState<EcmCensusParticipant | null>(null);
  const [detailsEditValues, setDetailsEditValues] = useState<EcmCensusEditValues | null>(null);
  const [pendingRemoveItem, setPendingRemoveItem] = useState<EcmCensusParticipant | null>(null);
  const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);
  const [deactivationDate, setDeactivationDate] = useState('');
  const [deactivationReason, setDeactivationReason] = useState('');
  const [isRequestingDeactivation, setIsRequestingDeactivation] = useState(false);

  const selectedMonth = parseMonthKey(selectedMonthKey);
  const { data, error, loading, refetch } = useQuery<EcmCensusQueryResult, EcmCensusQueryVariables>(ECM_CENSUS_QUERY, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      month: selectedMonth.month,
      year: selectedMonth.year,
    },
  });
  const {
    data: assigneeData,
    error: assigneeError,
    loading: assigneesLoading,
  } = useQuery<EcmCensusAssigneeOptionsResult>(ECM_CENSUS_ASSIGNEE_OPTIONS_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip: readOnly,
  });
  const [addCensusParticipant] = useMutation(ADD_ECM_CENSUS_PARTICIPANT_MUTATION);
  const [removeCensusParticipant] = useMutation(ECM_REMOVE_CENSUS_PARTICIPANT_MUTATION);
  const [requestParticipantDeactivation] = useMutation(ECM_REQUEST_DEACTIVATION_MUTATION);
  const [updateParticipantDetermination] = useMutation(ECM_UPDATE_DETERMINATION_MUTATION);
  const [updateCensusParticipant, { loading: isSavingDetails }] = useMutation(UPDATE_ECM_CENSUS_PARTICIPANT_MUTATION);

  const census = data?.ecmCensus;
  const rawItems = census?.items;
  const items = Array.isArray(rawItems) ? rawItems : [];
  const visibleColumnIds = resolveVisibleColumnIds(ecmDashboardColumnPreferenceMap?.census, ECM_CENSUS_COLUMNS.map((column) => column.id));
  const visibleColumnSet = new Set(visibleColumnIds);
  const visibleColumnCount = visibleColumnIds.length;
  const monthOptions = buildMonthOptions(census?.dateRangeStart, census?.dateRangeEnd);
  const selectedMonthLabel = getSelectedMonthLabel(selectedMonthKey, monthOptions);

  useEffect(() => {
    if (monthOptions.length === 0) {
      return;
    }

    const hasSelectedMonth = monthOptions.some((option) => option.value === selectedMonthKey);
    if (hasSelectedMonth) {
      return;
    }

    setSelectedMonthKey(monthOptions[0].value);
    setPage(1);
  }, [monthOptions, selectedMonthKey]);

  let filteredItems = items.filter((item) => {
    if (!matchesSelectedValue(item.program, programFilter)) {
      return false;
    }

    if (!matchesSelectedValue(item.facility, facilityFilter)) {
      return false;
    }

    if (!matchesSelectedValue(item.qa, qaFilter)) {
      return false;
    }

    if (!matchesSelectedValue(item.lcm, lcmFilter)) {
      return false;
    }

    const normalizedSearchText = normalizeLowerCase(searchText);
    if (!normalizedSearchText) {
      return true;
    }

    return getSearchableText(item).includes(normalizedSearchText);
  });

  filteredItems = [...filteredItems].sort((left, right) => {
    const sortValueComparison = compareValues(getSortValue(left, sortField), getSortValue(right, sortField), sortDirection);
    if (sortValueComparison !== 0) {
      return sortValueComparison;
    }

    const lastNameComparison = compareValues(normalizeValue(left.lastName), normalizeValue(right.lastName), 'asc');
    if (lastNameComparison !== 0) {
      return lastNameComparison;
    }

    return compareValues(normalizeValue(left.firstName), normalizeValue(right.firstName), 'asc');
  });

  const totalCount = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    if (page <= totalPages) {
      return;
    }

    setPage(totalPages);
  }, [page, totalPages]);

  // Pagination is temporarily disabled.
  // const startIndex = (page - 1) * PAGE_SIZE;
  // const visibleItems = filteredItems.slice(startIndex, startIndex + PAGE_SIZE);
  // const visibleStart = totalCount === 0 ? 0 : startIndex + 1;
  // const visibleEnd = totalCount === 0 ? 0 : startIndex + visibleItems.length;
  const visibleItems = filteredItems;
  const tableColumnCount = visibleColumnCount;

  const resolveErrorMessage = (error: unknown, fallbackMessage: string) => {
    if (error instanceof Error && normalizeValue(error.message)) {
      return error.message;
    }

    return fallbackMessage;
  };

  const runRowAction = async ({
    actionKey,
    action,
    errorMessage,
    rethrowOnError = false,
    successMessage,
  }: {
    actionKey: string;
    action: () => Promise<void>;
    errorMessage: string;
    rethrowOnError?: boolean;
    successMessage: string;
  }) => {
    setActiveRowActionKey(actionKey);
    try {
      await action();
      toast.success(successMessage);
      await refetch();
    } catch (error) {
      toast.error(resolveErrorMessage(error, errorMessage));
      if (rethrowOnError) {
        throw error;
      }
    } finally {
      setActiveRowActionKey(null);
    }
  };

  const addParticipantToCensus = async ({ odrparticipant }: { odrparticipant: number }) => {
    await runRowAction({
      action: async () => {
        const response = await addCensusParticipant({ variables: { odrparticipant } });
        if (!response.data?.addEcmCensusParticipant?.success) {
          throw new Error('Unable to add participant to Census.');
        }
      },
      actionKey: `add-${odrparticipant}`,
      errorMessage: 'Unable to add participant to Census.',
      rethrowOnError: true,
      successMessage: 'Participant added to Census.',
    });
  };

  const getParticipantActionLabel = (item: EcmCensusParticipant | null) => {
    if (!item) {
      return 'this participant';
    }

    return [normalizeValue(item.firstName), normalizeValue(item.lastName)].filter(Boolean).join(' ') || 'this participant';
  };

  const removeParticipantFromCensus = (item: EcmCensusParticipant) => {
    setPendingRemoveItem(item);
  };

  const openRowDetails = (item: EcmCensusParticipant) => {
    if (readOnly) return;
    setSelectedDetailsItem(item);
    setDetailsEditValues(getCensusEditValues(item));
  };

  const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>, item: EcmCensusParticipant) => {
    if (isInteractiveRowTarget(event.target)) {
      return;
    }

    openRowDetails(item);
  };

  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>, item: EcmCensusParticipant) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    openRowDetails(item);
  };

  const confirmRemoveParticipantFromCensus = async () => {
    if (!pendingRemoveItem) {
      return;
    }

    const item = pendingRemoveItem;
    setPendingRemoveItem(null);
    await runRowAction({
      action: async () => {
        const response = await removeCensusParticipant({ variables: { odrparticipant: item.odrparticipant } });
        if (!response.data?.removeEcmCensusParticipant?.success) {
          throw new Error('Unable to remove participant from Census.');
        }
      },
      actionKey: `remove-${item.odrparticipant}`,
      errorMessage: 'Unable to remove participant from Census.',
      successMessage: 'Participant removed from Census.',
    });
  };

  const placeOnHold = async (item: EcmCensusParticipant) => {
    const participantName = [normalizeValue(item.firstName), normalizeValue(item.lastName)].filter(Boolean).join(' ') || 'this participant';
    const actionConfirmed = window.confirm(`Are you sure you want to place ${participantName} on hold?`);

    if (!actionConfirmed) {
      return;
    }

    await runRowAction({
      action: async () => {
        const response = await updateParticipantDetermination({
          variables: { determination: 'on_hold', odrparticipant: item.odrparticipant },
        });
        if (!response.data?.updateEcmDetermination?.success) {
          throw new Error('Unable to place participant on hold.');
        }
      },
      actionKey: `hold-${item.odrparticipant}`,
      errorMessage: 'Unable to place participant on hold.',
      successMessage: 'Participant placed on hold.',
    });
  };

  const requestDeactivation = async () => {
    if (!selectedDetailsItem) {
      return;
    }

    const participantId = Number(selectedDetailsItem.participantId);
    if (!Number.isInteger(participantId) || participantId < 1) {
      toast.error('A participant program ID is required to request deactivation.');
      return;
    }

    if (!normalizeValue(deactivationDate) || !normalizeValue(deactivationReason)) {
      toast.error('Enter a deactivation date and reason.');
      return;
    }

    setIsRequestingDeactivation(true);
    try {
      const response = await requestParticipantDeactivation({
        variables: {
          input: {
            deactivationDate,
            deactivationReason,
            participant: participantId,
          },
          odrparticipant: selectedDetailsItem.odrparticipant,
        },
      });
      if (!response.data?.requestEcmDeactivation?.success) {
        throw new Error('Unable to request participant deactivation.');
      }
      toast.success('Deactivation requested.');
      setIsDeactivateDialogOpen(false);
      setDeactivationDate('');
      setDeactivationReason('');
      await refetch();
    } catch (requestError) {
      toast.error(resolveErrorMessage(requestError, 'Unable to request deactivation.'));
    } finally {
      setIsRequestingDeactivation(false);
    }
  };

  const saveParticipantDetails = async () => {
    if (!selectedDetailsItem || !detailsEditValues) {
      return;
    }

    if (!normalizeValue(detailsEditValues.qa) || !normalizeValue(detailsEditValues.lcm)) {
      toast.error('Select both an LCM and QA before saving.');
      return;
    }

    try {
      await updateCensusParticipant({
        variables: {
          input: detailsEditValues,
          odrparticipant: selectedDetailsItem.odrparticipant,
        },
      });
      toast.success('Census participant updated.');
      await refetch();
      setSelectedDetailsItem(null);
      setDetailsEditValues(null);
    } catch (mutationError) {
      toast.error(resolveErrorMessage(mutationError, 'Unable to update the census participant.'));
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(field);
    setSortDirection('asc');
  };

  const resetFilters = () => {
    setSearchText('');
    setProgramFilter(ALL_FILTER_VALUE);
    setFacilityFilter(ALL_FILTER_VALUE);
    setQaFilter(ALL_FILTER_VALUE);
    setLcmFilter(ALL_FILTER_VALUE);
    setSortField('lastName');
    setSortDirection('asc');
    setPage(1);
  };

  if (loading && !data) {
    return <OdrLoadingSpinner message="Loading ECM Census" />;
  }

  if (error && !census) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
        Unable to load the ECM census: {error.message}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Showing saved results while the latest refresh failed: {error.message}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-end gap-3 border-b border-slate-200 px-4 py-3">
          <div className="space-y-1">
            <FilterLabel>Month</FilterLabel>
            <Select
              value={selectedMonthKey}
              onValueChange={(value) => {
                setSelectedMonthKey(value);
                setPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-[190px] text-xs">
                <SelectValue aria-label="Month" />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.length === 0 ? (
                  <SelectItem value={selectedMonthKey}>{selectedMonthLabel}</SelectItem>
                ) : null}
                {monthOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <FilterLabel>Search</FilterLabel>
            <Input
              className="h-8 w-[240px] text-xs"
              placeholder="Name, CID, CIN, facility, notes"
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value);
                setPage(1);
              }}
            />
          </div>

          <div className="space-y-1">
            <FilterLabel>Program</FilterLabel>
            <Select
              value={programFilter}
              onValueChange={(value) => {
                setProgramFilter(value);
                setPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-[170px] text-xs">
                <SelectValue aria-label="Program" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_FILTER_VALUE}>All Programs</SelectItem>
                {(census?.programs || []).map((program) => (
                  <SelectItem key={program} value={program}>
                    {program}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <FilterLabel>Facility</FilterLabel>
            <Select
              value={facilityFilter}
              onValueChange={(value) => {
                setFacilityFilter(value);
                setPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-[180px] text-xs">
                <SelectValue aria-label="Facility" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_FILTER_VALUE}>All Facilities</SelectItem>
                {(census?.facilities || []).map((facility) => (
                  <SelectItem key={facility} value={facility}>
                    {facility}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <FilterLabel>QA</FilterLabel>
            <Select
              value={qaFilter}
              onValueChange={(value) => {
                setQaFilter(value);
                setPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-[150px] text-xs">
                <SelectValue aria-label="QA" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_FILTER_VALUE}>All QA</SelectItem>
                {(census?.qas || []).map((qa) => (
                  <SelectItem key={qa} value={qa}>
                    {qa}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <FilterLabel>LCM</FilterLabel>
            <Select
              value={lcmFilter}
              onValueChange={(value) => {
                setLcmFilter(value);
                setPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-[170px] text-xs">
                <SelectValue aria-label="LCM" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_FILTER_VALUE}>All LCM</SelectItem>
                {(census?.lcms || []).map((lcm) => (
                  <SelectItem key={lcm} value={lcm}>
                    {lcm}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <EcmResetButton onReset={resetFilters} />

          <div className="ml-auto flex items-center gap-2">
            {!readOnly && <Button className="h-8 px-1" size="sm" variant="link" onClick={() => setIsAddDialogOpen(true)}>
              Add to Census
            </Button>}
            {!readOnly && <EcmExportButton
              onExport={() => {
                exportEcmDataset({
                  columns: [...ECM_CENSUS_COLUMNS],
                  fileName: `ecm-census-${selectedMonthKey}`,
                  rows: items,
                });
              }}
            />}
            <EcmFieldSettingsMenu
              columns={[...ECM_CENSUS_COLUMNS]}
              visibleColumnIds={visibleColumnIds}
              onVisibleColumnsChange={(nextVisibleColumnIds) => {
                void updateEcmDashboardColumnPreferences('census', nextVisibleColumnIds);
              }}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-2 text-xs text-slate-600">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-slate-800">{selectedMonthLabel}</span>
            <span>•</span>
            <span>{totalCount} matching participants</span>
            <span>•</span>
            <span>{census?.totalCount || items.length} total for month</span>
          </div>

          {/* <div className="flex items-center gap-2">
            <span>
              Showing {visibleStart} to {visibleEnd} of {totalCount}
            </span>
            <Button
              aria-label="Previous page"
              className="h-8 px-2"
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span>
              Page {Math.min(page, totalPages)} of {totalPages}
            </span>
            <Button
              aria-label="Next page"
              className="h-8 px-2"
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div> */}
        </div>

        <Table className="min-w-[1600px]">
          <TableHeader className="bg-slate-50">
            <TableRow className="hover:bg-slate-50">
              {visibleColumnSet.has('firstName') ? (
                <TableHead><SortableHeader field="firstName" label="First Name" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead>
              ) : null}
              {visibleColumnSet.has('lastName') ? (
                <TableHead><SortableHeader field="lastName" label="Last Name" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead>
              ) : null}
              {visibleColumnSet.has('program') ? (
                <TableHead><SortableHeader field="program" label="Program" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead>
              ) : null}
              {visibleColumnSet.has('provider') ? (
                <TableHead><SortableHeader field="provider" label="Provider" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead>
              ) : null}
              {visibleColumnSet.has('censusStart') ? (
                <TableHead><SortableHeader field="censusStart" label="Date Entered" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead>
              ) : null}
              {visibleColumnSet.has('authorizationEndDate') ? (
                <TableHead><SortableHeader field="authorizationEndDate" label="Auth End Date" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead>
              ) : null}
              {visibleColumnSet.has('address') ? <TableHead>Address</TableHead> : null}
              {visibleColumnSet.has('phoneNumber') ? <TableHead>Phone #</TableHead> : null}
              {visibleColumnSet.has('notes') ? <TableHead>Notes</TableHead> : null}
              {visibleColumnSet.has('cid') ? (
                <TableHead><SortableHeader field="cid" label="CID" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead>
              ) : null}
              {visibleColumnSet.has('cin') ? (
                <TableHead><SortableHeader field="cin" label="CIN" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead>
              ) : null}
              {visibleColumnSet.has('healthPlan') ? (
                <TableHead><SortableHeader field="healthPlan" label="Health Plan Provider" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead>
              ) : null}
              {visibleColumnSet.has('facility') ? (
                <TableHead><SortableHeader field="facility" label="Facility" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead>
              ) : null}
              {visibleColumnSet.has('lcm') ? (
                <TableHead><SortableHeader field="lcm" label="LCM" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead>
              ) : null}
              {visibleColumnSet.has('dob') ? (
                <TableHead><SortableHeader field="dob" label="DOB" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead>
              ) : null}
              {visibleColumnSet.has('qa') ? (
                <TableHead><SortableHeader field="qa" label="QA" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead>
              ) : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleItems.length === 0 ? (
              <TableRow className="hover:bg-white">
                <TableCell className="py-10 text-center text-sm text-slate-500" colSpan={tableColumnCount}>
                  No participants match the current filters.
                </TableCell>
              </TableRow>
            ) : null}

            {visibleItems.map((item) => {
              const participantPath = toParticipantPath(item);
              const normalizedProgram = normalizeValue(item.program);
              const normalizedCid = normalizeValue(item.cid);
              const participantActionLabel = getParticipantActionLabel(item);
              const dischargedThisMonth = Boolean(item.dischargedThisMonth);

              return (
                <TableRow
                  aria-haspopup={readOnly ? undefined : 'dialog'}
                  aria-label={`View census details for ${participantActionLabel}`}
                  id={`ecm-census-row-${item.odrparticipant}-${item.participantId || 'none'}`}
                  key={`${item.odrparticipant}-${item.participantId || 'none'}`}
                  className={cn(
                    !readOnly && 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--azure-focus-ring)]',
                    dischargedThisMonth && 'bg-red-50 hover:bg-red-100'
                  )}
                  tabIndex={readOnly ? undefined : 0}
                  onClick={(event) => handleRowClick(event, item)}
                  onKeyDown={(event) => handleRowKeyDown(event, item)}
                >
                  {visibleColumnSet.has('firstName') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.firstName) || '—'}</TableCell> : null}
                  {visibleColumnSet.has('lastName') ? <TableCell className="whitespace-nowrap font-medium text-slate-900">{normalizeValue(item.lastName) || '—'}</TableCell> : null}
                  {visibleColumnSet.has('program') ? (
                    <TableCell className="whitespace-nowrap">
                      {!readOnly && participantPath && normalizedProgram ? (
                        <RouterLink className={PARTICIPANT_LINK_CLASS_NAME} to={participantPath}>
                          {normalizedProgram}
                        </RouterLink>
                      ) : (
                        normalizedProgram || '—'
                      )}
                    </TableCell>
                  ) : null}
                  {visibleColumnSet.has('provider') ? <TableCell className="max-w-[14rem]"><TextCell value={item.provider} /></TableCell> : null}
                  {visibleColumnSet.has('censusStart') ? <TableCell className="whitespace-nowrap">{formatDate(item.censusStart)}</TableCell> : null}
                  {visibleColumnSet.has('authorizationEndDate') ? <TableCell className="whitespace-nowrap">{formatDate(item.authorizationEndDate)}</TableCell> : null}
                  {visibleColumnSet.has('address') ? <TableCell className="max-w-[18rem]"><TextCell value={item.address} /></TableCell> : null}
                  {visibleColumnSet.has('phoneNumber') ? <TableCell className="whitespace-nowrap"><TextCell value={item.phoneNumber} /></TableCell> : null}
                  {visibleColumnSet.has('notes') ? <TableCell className="max-w-[18rem]"><TextCell value={item.notes} /></TableCell> : null}
                  {visibleColumnSet.has('cid') ? (
                    <TableCell className="whitespace-nowrap">
                      {!readOnly && participantPath && normalizedCid ? (
                        <RouterLink className={PARTICIPANT_LINK_CLASS_NAME} to={participantPath}>
                          {normalizedCid}
                        </RouterLink>
                      ) : (
                        normalizedCid || '—'
                      )}
                    </TableCell>
                  ) : null}
                  {visibleColumnSet.has('cin') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.cin) || '—'}</TableCell> : null}
                  {visibleColumnSet.has('healthPlan') ? <TableCell className="max-w-[14rem]"><TextCell value={item.healthPlan} /></TableCell> : null}
                  {visibleColumnSet.has('facility') ? <TableCell className="max-w-[14rem]"><TextCell value={item.facility} /></TableCell> : null}
                  {visibleColumnSet.has('lcm') ? <TableCell className="max-w-[12rem]"><TextCell value={item.lcm} /></TableCell> : null}
                  {visibleColumnSet.has('dob') ? <TableCell className="whitespace-nowrap">{formatDate(item.dob)}</TableCell> : null}
                  {visibleColumnSet.has('qa') ? <TableCell className="max-w-[12rem]"><TextCell value={item.qa} /></TableCell> : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <Dialog
        open={Boolean(selectedDetailsItem)}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setSelectedDetailsItem(null);
            setDetailsEditValues(null);
          }
        }}
      >
        <DialogContent
          className="overflow-hidden p-0"
          contentHeight="auto"
          contentMaxHeight="90vh"
          contentMaxWidth="95vw"
          contentWidth="72rem"
        >
          <DialogHeader className="border-b border-[var(--neutral-gray-30)] px-4 py-4 pr-12">
            <div className="flex min-h-8 items-center justify-between gap-4">
              <DialogTitle>Census Participant Details</DialogTitle>
              {selectedDetailsItem ? (
                <div aria-label="Record actions" className="flex items-center gap-2" role="toolbar">
                  <EcmRecordActionButton
                    disabled={isRequestingDeactivation}
                    icon={<UserMinus className="h-4 w-4" />}
                    label="Deactivate Participant"
                    onClick={() => setIsDeactivateDialogOpen(true)}
                  />
                  <EcmRecordActionButton
                    disabled={activeRowActionKey === `hold-${selectedDetailsItem.odrparticipant}`}
                    icon={<CirclePause className="h-4 w-4" />}
                    label="Place On Hold"
                    onClick={() => { void placeOnHold(selectedDetailsItem); }}
                  />
                  <EcmRecordActionButton
                    disabled={activeRowActionKey === `remove-${selectedDetailsItem.odrparticipant}`}
                    icon={<Trash2 className="h-4 w-4" />}
                    label="Remove from Census"
                    variant="destructive"
                    onClick={() => removeParticipantFromCensus(selectedDetailsItem)}
                  />
                </div>
              ) : null}
            </div>
            <DialogDescription className="sr-only">Census participant details</DialogDescription>
          </DialogHeader>
          <form
            id="ecm-census-details-form"
            className="grid max-h-[68vh] grid-cols-1 gap-x-6 gap-y-4 overflow-y-auto px-4 py-4 sm:grid-cols-2 lg:grid-cols-4"
            onSubmit={(event) => {
              event.preventDefault();
              void saveParticipantDetails();
            }}
          >
            {ECM_CENSUS_DETAIL_FIELDS.filter((field) => ![
              'address',
              'authorizationEndDate',
              'lcm',
              'notes',
              'phoneNumber',
              'qa',
            ].includes(field.id)).map((field) => (
              <div className={cn('min-w-0', field.fullWidth && 'sm:col-span-2 lg:col-span-4')} key={field.id}>
                <div className="text-xs font-semibold uppercase tracking-[0.04em] text-[var(--neutral-gray-80)]">
                  {field.label}
                </div>
                <div className="mt-1 whitespace-pre-wrap break-words text-sm text-[var(--neutral-gray-120)]">
                  {formatDetailValue(selectedDetailsItem?.[field.id], field.format)}
                </div>
              </div>
            ))}

            {detailsEditValues ? (
              <>
                <div className="min-w-0 space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-[0.04em] text-[var(--neutral-gray-80)]" htmlFor="ecm-census-auth-end-date">
                    Authorization End Date
                  </label>
                  <Input
                    id="ecm-census-auth-end-date"
                    type="date"
                    value={detailsEditValues.authorizationEndDate}
                    onChange={(event) => setDetailsEditValues((current) => current ? {
                      ...current,
                      authorizationEndDate: event.target.value,
                    } : current)}
                  />
                </div>
                <div className="min-w-0 space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-[0.04em] text-[var(--neutral-gray-80)]" htmlFor="ecm-census-phone-number">
                    Phone Number
                  </label>
                  <Input
                    id="ecm-census-phone-number"
                    maxLength={25}
                    value={detailsEditValues.phoneNumber}
                    onChange={(event) => setDetailsEditValues((current) => current ? {
                      ...current,
                      phoneNumber: event.target.value,
                    } : current)}
                  />
                </div>
                <div className="min-w-0 space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-[0.04em] text-[var(--neutral-gray-80)]" htmlFor="ecm-census-lcm">
                    LCM
                  </label>
                  <Select
                    value={detailsEditValues.lcm}
                    onValueChange={(value) => setDetailsEditValues((current) => current ? { ...current, lcm: value } : current)}
                  >
                    <SelectTrigger id="ecm-census-lcm">
                      <SelectValue placeholder={assigneesLoading ? 'Loading LCM users…' : 'Select LCM'} />
                    </SelectTrigger>
                    <SelectContent>
                      {(assigneeData?.ecmCensusAssigneeOptions?.lcms || []).map((assignee) => {
                        const name = normalizeValue(assignee.name);
                        if (!name) {
                          return null;
                        }
                        return <SelectItem key={normalizeValue(assignee.username) || name} value={name}>{name}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="min-w-0 space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-[0.04em] text-[var(--neutral-gray-80)]" htmlFor="ecm-census-qa">
                    QA
                  </label>
                  <Select
                    value={detailsEditValues.qa}
                    onValueChange={(value) => setDetailsEditValues((current) => current ? { ...current, qa: value } : current)}
                  >
                    <SelectTrigger id="ecm-census-qa">
                      <SelectValue placeholder={assigneesLoading ? 'Loading QA users…' : 'Select QA'} />
                    </SelectTrigger>
                    <SelectContent>
                      {(assigneeData?.ecmCensusAssigneeOptions?.qas || []).map((assignee) => {
                        const name = normalizeValue(assignee.name);
                        if (!name) {
                          return null;
                        }
                        return <SelectItem key={normalizeValue(assignee.username) || name} value={name}>{name}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="min-w-0 space-y-1 sm:col-span-2 lg:col-span-4">
                  <label className="text-xs font-semibold uppercase tracking-[0.04em] text-[var(--neutral-gray-80)]" htmlFor="ecm-census-address">
                    Address
                  </label>
                  <Textarea
                    id="ecm-census-address"
                    rows={2}
                    value={detailsEditValues.address}
                    onChange={(event) => setDetailsEditValues((current) => current ? {
                      ...current,
                      address: event.target.value,
                    } : current)}
                  />
                </div>
                <div className="min-w-0 space-y-1 sm:col-span-2 lg:col-span-4">
                  <label className="text-xs font-semibold uppercase tracking-[0.04em] text-[var(--neutral-gray-80)]" htmlFor="ecm-census-notes">
                    Notes
                  </label>
                  <Textarea
                    id="ecm-census-notes"
                    rows={4}
                    value={detailsEditValues.notes}
                    onChange={(event) => setDetailsEditValues((current) => current ? {
                      ...current,
                      notes: event.target.value,
                    } : current)}
                  />
                </div>
                {assigneeError ? (
                  <div className="text-sm text-red-700 sm:col-span-2 lg:col-span-4">
                    Unable to load tagged LCM and QA users: {assigneeError.message}
                  </div>
                ) : null}
              </>
            ) : null}

          </form>
          <DialogFooter className="border-t border-[var(--neutral-gray-30)] px-4 py-3">
            <Button
              disabled={isSavingDetails || assigneesLoading || Boolean(assigneeError)}
              form="ecm-census-details-form"
              id="ecm-census-details-save"
              size="sm"
              type="submit"
            >
              <Save className="h-4 w-4" />
              {isSavingDetails ? 'Saving…' : 'Save Changes'}
            </Button>
            <Button
              id="ecm-census-details-close"
              size="sm"
              variant="outline"
              onClick={() => {
                setSelectedDetailsItem(null);
                setDetailsEditValues(null);
              }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isDeactivateDialogOpen}
        onOpenChange={(open: boolean) => {
          setIsDeactivateDialogOpen(open);
          if (!open) {
            setDeactivationDate('');
            setDeactivationReason('');
          }
        }}
      >
        <DialogContent contentHeight="auto" contentMaxHeight="90vh" contentMaxWidth="95vw" contentWidth="32rem">
          <DialogHeader>
            <DialogTitle>Deactivate Participant</DialogTitle>
            <DialogDescription className="sr-only">Enter the deactivation date and reason</DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            id="ecm-census-deactivation-form"
            onSubmit={(event) => {
              event.preventDefault();
              void requestDeactivation();
            }}
          >
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase tracking-[0.04em] text-[var(--neutral-gray-80)]" htmlFor="ecm-census-deactivation-date">
                Deactivation Date
              </label>
              <Input
                id="ecm-census-deactivation-date"
                min={new Date().toISOString().slice(0, 10)}
                required
                type="date"
                value={deactivationDate}
                onChange={(event) => setDeactivationDate(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase tracking-[0.04em] text-[var(--neutral-gray-80)]" htmlFor="ecm-census-deactivation-reason">
                Reason
              </label>
              <Textarea
                id="ecm-census-deactivation-reason"
                maxLength={255}
                required
                rows={3}
                value={deactivationReason}
                onChange={(event) => setDeactivationReason(event.target.value)}
              />
            </div>
          </form>
          <DialogFooter>
            <Button disabled={isRequestingDeactivation} form="ecm-census-deactivation-form" size="sm" type="submit">
              {isRequestingDeactivation ? 'Submitting…' : 'Submit'}
            </Button>
            <Button
              size="sm"
              type="button"
              variant="outline"
              onClick={() => {
                setIsDeactivateDialogOpen(false);
                setDeactivationDate('');
                setDeactivationReason('');
              }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <EcmAddParticipantDialog
        description="Search by participant name, CID, or CIN, then add the participant to the active census."
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onSubmit={addParticipantToCensus}
        submitLabel="Add to Census"
        title="Add Participant to Census"
      />
      <AlertDialog open={Boolean(pendingRemoveItem)} onOpenChange={(open) => {
        if (!open) {
          setPendingRemoveItem(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove participant from Census</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {getParticipantActionLabel(pendingRemoveItem)}
              {normalizeValue(pendingRemoveItem?.cin) ? ` (CIN ${normalizeValue(pendingRemoveItem?.cin)})` : ''} from Census?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row">
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={() => {
                void confirmRemoveParticipantFromCensus();
              }}
            >
              Remove
            </AlertDialogAction>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
